import { useCart, useCustomerAuth } from '@agos/customer-core'
import { useGetCustomerAuthPersonalizedSuggestion } from '@agos/api-client/customers'
import { T, formatCents, tt } from '@agos/shared'
import { Check, Sparkles, Tag, UtensilsCrossed, X } from 'lucide-react'
import { useState } from 'react'

import {
  couponNote,
  discountGreeting,
  greetingOf,
  mayAddDirectly,
  varietyGreeting,
  type GreetingDiscountCard,
} from './greeting'

/**
 * The two cards the home page shows somebody it recognises.
 *
 * `HomePage.tsx` passed `greetingName={null}` as a literal, so this section rendered for **nobody** —
 * a franchise switching it on got nothing, and the rules and templates behind it were dead code. That
 * is the eighth time in this port that something was written, tested and connected to nothing.
 *
 * Both cards are dismissable and neither returns after being dismissed within the visit, as legacy has
 * it: a suggestion that cannot be waved away is an advertisement.
 *
 * The discount card offers to add the dish **only when it is still on the menu at a price**. The server
 * sends `price: null` for one that has been withdrawn since the order that earned the suggestion, and
 * adding it at a price nobody set is FB-C29's mistake in a different jacket.
 */

const CARD = 'rounded-2xl border p-4 flex items-start gap-3'

export function PersonalizedGreeting(props: { onOpenCategory?: (categoryId: number | null) => void }) {
  const signedIn = useCustomerAuth((state) => state.user) !== null
  const addLine = useCart((state) => state.add)

  const { data } = useGetCustomerAuthPersonalizedSuggestion({
    query: { enabled: signedIn, retry: false, staleTime: 300_000 },
  })

  const [dismissed, setDismissed] = useState<{ discount: boolean; variety: boolean }>({
    discount: false,
    variety: false,
  })
  const [added, setAdded] = useState(false)
  const [copied, setCopied] = useState(false)

  const greeting = greetingOf(data)
  if (!signedIn || greeting === null) return null

  const now = new Date()
  const showDiscount = greeting.discount !== null && !dismissed.discount
  const showVariety = greeting.variety !== null && !dismissed.variety
  if (!showDiscount && !showVariety) return null

  function addDiscount(card: GreetingDiscountCard) {
    if (!mayAddDirectly(card)) {
      props.onOpenCategory?.(card.categoryId)
      return
    }
    addLine({ productId: card.productId, extras: '', quantity: 1 })
    setAdded(true)
  }

  return (
    <div className="space-y-3" data-greeting>
      {showDiscount && greeting.discount !== null && (
        <div className={`${CARD} bg-primary/5 border-primary/20`} data-greeting-discount>
          <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-sm font-semibold" data-greeting-line>
              {discountGreeting(greeting, now)}
            </p>
            {greeting.discount.coupon !== null && (
              <button
                type="button"
                data-greeting-coupon={greeting.discount.coupon.code}
                onClick={() => {
                  // `writeText` can be refused — an insecure origin, a denied permission — and a card
                  // that silently does nothing is worse than one that never offered.
                  void navigator.clipboard
                    ?.writeText(greeting.discount?.coupon?.code ?? '')
                    .then(() => setCopied(true))
                    .catch(() => setCopied(false))
                }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary"
              >
                <Tag className="w-3.5 h-3.5" />
                {greeting.discount.coupon.code}
                {couponNote(greeting.discount.coupon, formatCents) !== '' && (
                  <span className="opacity-70">
                    · {couponNote(greeting.discount.coupon, formatCents)}
                  </span>
                )}
                {copied && <Check className="w-3.5 h-3.5" />}
              </button>
            )}
            <button
              type="button"
              data-greeting-add={greeting.discount.productId}
              onClick={() => greeting.discount !== null && addDiscount(greeting.discount)}
              className="block min-h-[36px] rounded-xl bg-primary px-3 text-xs font-bold text-primary-foreground"
            >
              {added ? (
                <T>Pridané!</T>
              ) : mayAddDirectly(greeting.discount) ? (
                <T>Pridať do košíka</T>
              ) : (
                <T>Pozrieť</T>
              )}
            </button>
          </div>
          <button
            type="button"
            data-greeting-dismiss="discount"
            aria-label={tt('Zavrieť')}
            onClick={() => setDismissed({ ...dismissed, discount: true })}
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {showVariety && greeting.variety !== null && (
        <div className={`${CARD} bg-card border-border`} data-greeting-variety>
          <div className="w-10 h-10 rounded-xl bg-green-600/10 text-green-600 flex items-center justify-center shrink-0">
            <UtensilsCrossed className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-sm font-semibold" data-greeting-line>
              {varietyGreeting(greeting, now)}
            </p>
            <button
              type="button"
              data-greeting-look={greeting.variety.productId}
              onClick={() => props.onOpenCategory?.(greeting.variety?.categoryId ?? null)}
              className="block min-h-[36px] rounded-xl border border-border px-3 text-xs font-semibold"
            >
              <T>Pozrieť</T>
            </button>
          </div>
          <button
            type="button"
            data-greeting-dismiss="variety"
            aria-label={tt('Zavrieť')}
            onClick={() => setDismissed({ ...dismissed, variety: true })}
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
